// Story 11.8 — the ONE label map per column for Screens 20 + 21 (AC3, AC5).
//
// VOCABULARY SOURCING (boundary #2, both allowed options used where each fits):
//   • Calibration and live-scoring pills IMPORT E14's shipped maps verbatim
//     (`dashboard-derivations.ts` `CALIBRATION_PILL` / `calibrationPill` /
//     `liveScoringPill`) — the labels are identical on both admin surfaces, so one
//     vocabulary and no edit.
//   • Access state is MIRRORED here with this mirror note, NOT imported. E14's
//     `ACCOUNT_STATE_PILL` is SEAT vocabulary ("Assigned", "Blocked (CITS)") and it
//     fails SOFT — `accountStatePill(undefined)` returns the ACTIVE pill. The
//     platform-admin column is the access-state discriminator on rows that may have
//     no org at all, the client-validated Screen 20 says Enabled / Blocked / Pending
//     (`amps-screen-20-admin-users.html:93-97`), and AC3 requires an unrecognised
//     value to fail CLOSED to `blocked "Unknown state"`. Importing would have
//     mislabelled a seatless super_admin "Assigned" and rendered a garbage state as
//     healthy. `dashboard-derivations.ts` is NOT edited (shared-helper rule).
//
// The `already_certified` branch is ADDED here wrapping the import, per boundary #2.

import {
  calibrationPill,
  liveScoringPill,
  UNKNOWN_ACCOUNT_STATE_PILL,
} from "@/routes/org-admin/dashboard-derivations";
import { ROLE_PILL_TEXT } from "@/components/layout/nav-config";
import type {
  AdminAccessState,
  AdminCalibrationState,
  AdminLiveScoring,
  AdminUserSubscriptionBlock,
} from "@/hooks/queries/admin-users-types";

/** Every pill this unit renders. `variant` is a `Badge` variant name. */
export interface AdminPill {
  variant: "ok" | "pending" | "blocked" | "info" | "warning" | "neutral";
  label: string;
}

/** The em-dash placeholder AC3 requires for an absent value. */
export const EMPTY_CELL = "—";

// ---------------------------------------------------------------------------
// Role
// ---------------------------------------------------------------------------

// `ROLE_PILL_TEXT` deliberately carries no `clinician` entry (gh-625: the topbar
// pill is admin-only). The directory's Role COLUMN has to name all three platform
// roles, so the clinician label is added here rather than in `nav-config.ts` —
// re-adding it there would resurface a string gh-625 removed on purpose.
export const ADMIN_ROLE_LABEL: Record<string, string> = {
  ...ROLE_PILL_TEXT,
  clinician: "Clinician",
};

/** An unknown role renders VERBATIM — never a friendlier guess. */
export function roleLabel(role: string): string {
  return ADMIN_ROLE_LABEL[role] ?? role;
}

/** Boundary #4 option (a): the three platform roles, plus the all-roles clear. */
export const ROLE_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "", label: "All roles" },
  { value: "clinician", label: ADMIN_ROLE_LABEL.clinician },
  { value: "org_admin", label: ADMIN_ROLE_LABEL.org_admin },
  { value: "super_admin", label: ADMIN_ROLE_LABEL.super_admin },
];

// ---------------------------------------------------------------------------
// Access state
// ---------------------------------------------------------------------------

// The mirrored map (see the header note). `removed` and `erased` render as pills
// but get no filter chip (boundary #9) — the client-validated Screen 20 offers
// three, and neither is a state an admin filters a working directory by.
//
// `erased` is its OWN pill and not a second spelling of `removed`: a GDPR Art. 17
// tombstone is irreversible, and "Removed" reads as a seat an admin can restore.
export const ADMIN_ACCESS_STATE_PILL: Record<AdminAccessState, AdminPill> = {
  active: { variant: "ok", label: "Enabled" },
  invited: { variant: "pending", label: "Pending" },
  blocked: { variant: "blocked", label: "Blocked" },
  removed: { variant: "blocked", label: "Removed" },
  erased: { variant: "blocked", label: "Erased" },
};

/**
 * Fails CLOSED — an absent or unrecognised state renders the shared
 * `UNKNOWN_ACCOUNT_STATE_PILL` ("Unknown state", blocked), never the healthy pill.
 * The row that most needs attention must not carry the most reassuring label.
 */
export function adminAccessStatePill(state?: AdminAccessState | string): AdminPill {
  return ADMIN_ACCESS_STATE_PILL[state as AdminAccessState] ?? UNKNOWN_ACCOUNT_STATE_PILL;
}

/**
 * Boundary #9: three chips, mapping the client-validated Screen 20 labels onto the
 * wire values.
 *
 * The backend serves FIVE: `?status=` takes `active | invited | blocked | removed |
 * erased` (`administration/list_users/schemas.py` `AccessState`, and
 * `?status=removed` is asserted 200-with-a-row in that slice's endpoint
 * integration tests). This list exposes THREE of them because that is what the
 * client-validated mockup carries — `amps-screen-20-admin-users.html` offers
 * exactly `All access states / Enabled / Blocked / Pending`, and `erased` did not
 * exist as a state when that screen was validated. Adding "Removed"/"Erased" chips
 * is therefore a screen change needing client sign-off, not a dev fix; do not
 * "restore" them to match the wire vocabulary.
 *
 * The two unexposed values stay reachable by hand-editing the URL, and
 * `filtersFromParams` passes any truthy status straight through. That used to
 * render the `<Select>` BLANK while the filter was silently applied — the rows
 * correct, the control unable to say what it was doing. `AdminUsersListScreen`
 * now appends a TRANSIENT option for a status absent from this list, labelled
 * through `adminAccessStatePill` so the control and the rows read from one map
 * and an unrecognised value inherits the same fail-closed "Unknown state".
 *
 * That is deliberately not the same thing as exposing the values: this list is
 * still exactly the validated four, and a plain list still offers only those.
 * The transient option names a filter the operator already asked for; adding
 * chips would offer one they did not.
 */
export const ACCESS_STATE_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "", label: "All access states" },
  { value: "active", label: "Enabled" },
  { value: "blocked", label: "Blocked" },
  { value: "invited", label: "Pending" },
];

// ---------------------------------------------------------------------------
// Subscription
// ---------------------------------------------------------------------------

// One label per SHIPPED Stripe status (11.1's `SubscriptionStatus` enum), and no
// client-side lapse re-derivation (AC3 divergence (b)): the label comes from
// `subscription.status` alone, so a stored-`active` row whose period has ended
// still reads "Active" here while the DETAIL screen's resolved `live_scoring` is
// the authoritative answer. Collapsing the six unhealthy statuses into the
// mockup's single "Lapsed" would BE that re-derivation and would tell a support
// admin "lapsed" for a card that merely needs a retry.
export const SUBSCRIPTION_PILL: Record<string, AdminPill> = {
  active: { variant: "ok", label: "Active" },
  trialing: { variant: "ok", label: "Trialing" },
  pending_calibration: { variant: "pending", label: "Pending calibration" },
  past_due: { variant: "warning", label: "Past due" },
  paused: { variant: "warning", label: "Paused" },
  incomplete: { variant: "warning", label: "Incomplete" },
  canceled: { variant: "blocked", label: "Canceled" },
  unpaid: { variant: "blocked", label: "Unpaid" },
  incomplete_expired: { variant: "blocked", label: "Expired" },
};

/** No billing row at all — the mockup's `info "N/A"` admin row (`:97`). */
export const NO_SUBSCRIPTION_PILL: AdminPill = { variant: "info", label: "N/A" };

export function subscriptionPill(subscription?: AdminUserSubscriptionBlock): AdminPill {
  const status = subscription?.status;
  // Absent status = no billing row. Distinct from an unrecognised one, which is a
  // state we cannot vouch for and therefore fails closed.
  if (status == null) return NO_SUBSCRIPTION_PILL;
  return SUBSCRIPTION_PILL[status] ?? UNKNOWN_ACCOUNT_STATE_PILL;
}

/**
 * Story 16.7 AC15 — the statuses that mean MONEY HAS LANDED, mirroring the
 * server's own `billing.LIVE_SUBSCRIPTION_STATUSES` (`billing/contract.py`).
 * The three are exactly the ones `has_completed_checkout` treats as live, and
 * the mirror is deliberate duplication rather than a shared constant: this file
 * is a rendering vocabulary and the backend one is a money predicate, and the
 * only thing they are allowed to share is the list itself.
 *
 * THE SERVER REMAINS THE FENCE. This predicate never authorises anything — it
 * only decides whether to OFFER the enable-paid-account control, so that Screen
 * 21 does not put a permanently-refusing button on the several thousand accounts
 * that will never satisfy ADMIN-017. A client that got this wrong shows or hides
 * a button; the refusal that matters is `has_completed_checkout`'s.
 *
 * Read off the LIST/DETAIL `subscription.status`, which is the newest billing
 * row's stored status. That is a weaker signal than the server's own read (it
 * cannot see period ends), which is the correct direction for a control gate:
 * it may offer a button the server then refuses, and it may not hide one the
 * server would have honoured.
 */
export const LIVE_SUBSCRIPTION_STATUSES: readonly string[] = [
  "active",
  "trialing",
  "pending_calibration",
];

export function hasLiveSubscriptionRow(subscription?: AdminUserSubscriptionBlock): boolean {
  const status = subscription?.status;
  return status != null && LIVE_SUBSCRIPTION_STATUSES.includes(status);
}

// ---------------------------------------------------------------------------
// Calibration
// ---------------------------------------------------------------------------

/**
 * Boundary #8: the `already_certified` bypass OUTRANKS the raw state. 11.1 put the
 * boolean on the wire precisely so this surface would not tell a support admin that
 * a clinician certified before the platform "has not started".
 *
 * Everything else delegates to E14's shipped `calibrationPill`, including the
 * "{N}/{M} cases" literal and its missing-count degradation.
 */
export function adminCalibrationPill(calibration: {
  state: AdminCalibrationState;
  completed_cases?: number | null;
  required_case_count: number;
  already_certified?: boolean | null;
}): AdminPill {
  if (calibration.already_certified) return { variant: "info", label: "N/A" };
  return calibrationPill(
    calibration.state,
    calibration.completed_cases ?? undefined,
    calibration.required_case_count,
  );
}

/** Screen 21's sentence form of the same decision (boundary #8). */
export function adminCalibrationStatusLine(calibration: {
  state: AdminCalibrationState;
  completed_cases?: number | null;
  required_case_count: number;
  already_certified?: boolean | null;
}): string {
  if (calibration.already_certified) {
    return "Not applicable (certified before the platform)";
  }
  if (calibration.state === "complete") return "Complete";
  if (calibration.state === "in_progress") {
    return calibration.completed_cases != null
      ? `${calibration.completed_cases} / ${calibration.required_case_count} case studies complete`
      : "In progress";
  }
  return "Not started";
}

// ---------------------------------------------------------------------------
// Live scoring (detail only) — imported vocabulary, fails CLOSED already.
// ---------------------------------------------------------------------------

export function adminLiveScoringPill(state?: AdminLiveScoring): AdminPill {
  return liveScoringPill(state);
}
